import { notification } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Cookies from 'js-cookie';

import { AdvocatePageType, AppCode, Role } from '../enum';
import { environment } from '../environment/environment';
import type { FilteredInfo } from '../models/table';
import { RaygunErrorHandlerService } from '../service/raygun.service';

dayjs.extend(utc);
dayjs.extend(timezone);

const { logError } = RaygunErrorHandlerService();

export const isUndefinedOrNull = <T>(value: T) => {
  return value === undefined || value === null;
};

export const removeEmpty = (obj: FilteredInfo) => {
  if (!obj) return {};
  // oxlint-disable-next-line typescript/no-dynamic-delete
  Object.keys(obj).forEach((key) => obj[key] == null && delete obj[key]);
  return obj;
};

export const isValidUrl = (urlStr: string) => {
  try {
    new URL(urlStr);
    return true;
  } catch (e) {
    logError(e, ['utils.helper', 'isValidUrl']);
    return false;
  }
};

export const getTableFilters = (filteredInfo: FilteredInfo) => {
  const filters = [];
  for (const filter in filteredInfo) {
    if (Object.prototype.hasOwnProperty.call(filteredInfo, filter)) {
      const dateformat = /\d{4}-\d{2}-\d{2}/;
      const pretty_field_name = filter.replaceAll('_', ' ');
      const filter_value_separator =
        filteredInfo[filter].length > 0 && filteredInfo[filter][0].toString().match(dateformat) ? ' - ' : '-';
      filters.push(`${pretty_field_name} ${filteredInfo[filter].join(filter_value_separator)}`);
    }
  }
  if (filters.length > 0) {
    return ` (${filters.join(', ')})`;
  }
  return '';
};

export const validateAsAdmin = (role: Role | null) => role === Role.SUPER_ADMIN || role === Role.EXTERNAL_ADMIN;
export const validateAsHighRole = (role: Role | null) => validateAsAdmin(role) || role === Role.DIRECTOR;
export const validateAsRegionalRole = (role: Role | null) => role === Role.DIRECTOR || role === Role.MANAGER;
export const validateAsFirstAlertsRole = (role: Role | null) =>
  role === Role.ALERT_MANAGER || role === Role.ALERT_DISPATCHER;
export const validateAsSPManagerRole = (role: Role | null) => role === Role.SERVICE_PROVIDER_MANAGER;

export const getAdvocateLabel = ({
  role,
  plural = false,
  sentenceCase = false,
  advocatePageType = AdvocatePageType.PeerAdvocate,
}: {
  role: Role | null;
  plural?: boolean;
  sentenceCase?: boolean;
  advocatePageType?: AdvocatePageType;
}) => {
  const validRole = validateAsAdmin(role) || role === Role.DASHBOARD_ONLY || role === Role.SERVICE_PROVIDER_MANAGER;
  const label = plural
    ? validRole && advocatePageType === AdvocatePageType.ServiceProvider
      ? 'service providers'
      : 'peers'
    : validRole && advocatePageType === AdvocatePageType.ServiceProvider
      ? 'service provider'
      : 'peer';

  return sentenceCase ? label.charAt(0).toUpperCase() + label.slice(1) : label;
};

export const getAdvocatePageType = (pathname: string) => {
  return pathname.includes('service-provider') ? AdvocatePageType.ServiceProvider : AdvocatePageType.PeerAdvocate;
};

export const showValidationError = (title: string, message: string) => {
  if (!title) title = 'Error';
  notification.error({
    title,
    description: message,
  });
};

export const notifyMissingFields = (missingRequiredFields: string[]) => {
  const lastFieldName = missingRequiredFields.pop();
  let fieldNames = lastFieldName;
  if (missingRequiredFields.length > 0) {
    fieldNames =
      missingRequiredFields.join(', ') + `${missingRequiredFields.length > 1 ? ',' : ''} and ${lastFieldName}`;
  }
  showValidationError('Missing required fields', `Missing a value for ${fieldNames}.`);
  return false;
};

const hostname = environment.hostname || new URL(window.location.origin).hostname;
export const isBQDomain = hostname.includes('buddysquest');
export const isAdminSubDomain = hostname.includes('admin');

export const appCode = isAdminSubDomain ? null : isBQDomain ? AppCode.BuddysQuest : AppCode.TalkingStick;

export const appName = (app_code: AppCode | null) => {
  const the_app_code = app_code || appCode;
  return the_app_code === AppCode.BuddysQuest ? "Buddy's Quest" : 'Talking Stick';
};

export const dateFormatToLocale = (
  timestamp: Date | Dayjs | string | null | undefined,
  month: 'short' | 'long' = 'short',
): string => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return new Intl.DateTimeFormat(navigator.language, {
    month: month,
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date.toDate());
};

export const timeFormatToLocale = (timestamp: Date | Dayjs | string | null | undefined): string => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return new Intl.DateTimeFormat(navigator.language, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date.toDate());
};

export const dateFormatNoTimeToLocale = (timestamp: Date | Dayjs | string | null | undefined): string => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return new Intl.DateTimeFormat(navigator.language, {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(date.toDate());
};

export const formatDateTimeByTimezone = (
  timestamp: Date | Dayjs | string | null | undefined,
  timezone_name: string | null | undefined,
  format: string = `MMM DD, YYYY h:mm A ${timezone_name ? '(z)' : ''}`,
) => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return date.tz(timezone_name ?? undefined).format(format);
};

export const formatTimeByTimezone = (
  timestamp: Date | Dayjs | string | null | undefined,
  timezone_name: string | null | undefined,
) => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return date.tz(timezone_name ?? undefined).format(`h:mm:ss A ${timezone_name ? '(z)' : ''}`);
};

export const formatDateNoTimeByTimezone = (
  timestamp: Date | Dayjs | string | null | undefined,
  timezone_name: string | null | undefined,
) => {
  const date = dayjs(timestamp);
  if (!date.isValid()) return '';
  return date.tz(timezone_name ?? undefined).format(`MMM DD, YYYY ${timezone_name ? '(z)' : ''}`);
};

export const getAbbreviatedTimezone = (timezone_name: string | null | undefined) => {
  return timezone_name ? dayjs().tz(timezone_name).format('z') : '';
};

export const getCSVFilename = (title: string) => {
  return `${title}.csv`;
};

type IDictionary = Record<string, string>;

export const getCSV = (data: object[], headers: string[]) => {
  if (!data || !headers) return '';
  let csv = `${headers.join(',')}\n`;
  for (const obj of data) {
    const line = headers
      .filter((key) => key in obj)
      .map((key) => (obj as IDictionary)[key])
      .map((key) => (key ? key.toString().replace(/,/gi, '/') : key))
      .join(',');
    csv += line;
    csv += '\n';
  }
  return csv;
};

export const removeCookies = () => {
  const [_subdomain, rootDomain, topLevelDomain] = location.hostname.split('.');
  const CookiesAPI = Cookies.withAttributes({ path: '/', domain: `.${rootDomain}.${topLevelDomain}` });
  const names = Object.keys(CookiesAPI.get());
  for (const name of names) {
    CookiesAPI.remove(name);
  }
};
